import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, ArrowLeft } from 'lucide-react';
import { api } from '../services/api';
import SEO from '../components/SEO';

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'no-token'>('loading');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await api.verifyEmail(token);
        setStatus('success');
        setMessage(response.message);
        setEmail(response.email);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (err) {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Verification failed');
      }
    };

    verifyEmail();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
      <SEO
        title="Verify Email"
        description="Verify your email address to activate your My Investments account."
        canonical="https://my-investments.io/verify-email"
      />
      <div className="max-w-md w-full">
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-500/10 mb-6">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Verifying Email</h1>
              <p className="text-slate-400">Please wait while we verify your email address...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Email Verified!</h1>
              <p className="text-slate-400 mb-4">{message}</p>
              {email && (
                <p className="text-sm text-slate-500 mb-6">
                  Verified email: <span className="text-teal-400">{email}</span>
                </p>
              )}
              <p className="text-sm text-slate-500">Redirecting to login...</p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 mt-4 text-teal-400 hover:text-teal-300 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Go to Login Now
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-6">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Verification Failed</h1>
              <p className="text-slate-400 mb-6">{message}</p>
              <p className="text-sm text-slate-500 mb-4">
                The link may have expired or is invalid. Please request a new verification email.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Go to Login
                </Link>
                <Link
                  to="/resend-verification"
                  className="inline-flex items-center justify-center gap-2 text-teal-400 hover:text-teal-300 transition-colors"
                >
                  Request New Verification Email
                </Link>
              </div>
            </>
          )}

          {status === 'no-token' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mb-6">
                <Mail className="w-8 h-8 text-amber-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">No Verification Token</h1>
              <p className="text-slate-400 mb-6">
                This page requires a verification token. Please use the link from your verification email.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Go to Login
                </Link>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-slate-500 text-sm mt-6">
          Having trouble?{' '}
          <a href="mailto:support@marome-investments.co.za" className="text-teal-400 hover:text-teal-300">
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
};

export default VerifyEmail;
